import React, { useCallback, useEffect, useState } from "react";
import { StatusBar } from "expo-status-bar";
import * as SplashScreen from "expo-splash-screen";
import * as Linking from "expo-linking";
import { useFonts } from "expo-font";
import { SafeAreaProvider } from "react-native-safe-area-context";

import useCachedResources from "./hooks/useCachedResources";
import useColorScheme from "./hooks/useColorScheme";
import Navigation from "./navigation";
import { PaperProvider } from 'react-native-paper';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { en, registerTranslation } from 'react-native-paper-dates';
registerTranslation('en', en);

import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  split,
  HttpLink,
  from,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { getMainDefinition } from "@apollo/client/utilities";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { createClient } from "graphql-ws";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { getStack } from "./constants/Stacks";
import { loadAppTheme, subscribeAppTheme } from "./utils/themeDesigner";
import { emitAuthFailure } from "./utils/authEvents";

SplashScreen.preventAutoHideAsync();

const stack = getStack();

const getSubscriptionConnectionParams = async () => {
  const [token, adminToken, operatorId] = await Promise.all([
    AsyncStorage.getItem("token").catch(() => null),
    AsyncStorage.getItem("adminToken").catch(() => null),
    AsyncStorage.getItem("operatorId").catch(() => null),
  ]);

  return {
    token: token || undefined,
    adminToken: adminToken || undefined,
    operatorId: operatorId || undefined,
    subscriptionKey: process.env.EXPO_PUBLIC_SUBSCRIPTION_KEY || undefined,
  };
};

const wsLink = new GraphQLWsLink(
  createClient({
    url: stack.subscriptions,
    connectionParams: getSubscriptionConnectionParams,
  })
);

const httpLink = new HttpLink({
  uri: stack.graphql,
});

// Intercept GraphQL errors to detect expired / invalid tokens.
// When an auth error is detected, clear stored credentials so the
// navigation auth guard redirects to the login screen.
const authErrorLink = onError(({ graphQLErrors, networkError }) => {
  let isAuthFailure = false;

  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      const code = err.extensions?.code;
      const msg = (err.message || "").toLowerCase();
      if (
        code === "UNAUTHENTICATED" ||
        code === "FORBIDDEN" ||
        (msg.includes("token") && (msg.includes("expired") || msg.includes("invalid"))) ||
        msg.includes("unauthorized") ||
        msg.includes("unauthenticated")
      ) {
        isAuthFailure = true;
        break;
      }
    }
  }

  if (networkError) {
    const status = networkError.statusCode || networkError.status;
    if (status === 401 || status === 403) {
      isAuthFailure = true;
    }
  }

  if (isAuthFailure) {
    console.warn("[Auth] Token expired or invalid – clearing credentials");
    const keys = ["token", "operatorId", "adminToken", "role"];
    Promise.all(keys.map((k) => AsyncStorage.removeItem(k).catch(() => {}))).then(() => {
      emitAuthFailure();
    });
  }
});

// The split function takes three parameters:
//
// * A function that's called for each operation to execute
// * The Link to use for an operation if the function returns a "truthy" value
// * The Link to use for an operation if the function returns a "falsy" value
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === "OperationDefinition" &&
      definition.operation === "subscription"
    );
  },
  wsLink,
  httpLink
);

const client = new ApolloClient({
  link: from([authErrorLink, splitLink]),
  cache: new InMemoryCache(),
  // cache: new Hermes(),
});

export default function App() {
  const url = Linking.useURL();
  if (url) {
    const { hostname, path, queryParams } = Linking.parse(url);

    console.log(
      `Linked to app with hostname: ${hostname}, path: ${path} and data: ${JSON.stringify(
        queryParams
      )}`
    );
  }

  const isLoadingComplete = useCachedResources();
  const colorScheme = useColorScheme();
  const [isThemeReady, setIsThemeReady] = useState(false);
  const [themeVersion, setThemeVersion] = useState(0);

  useEffect(() => {
    let mounted = true;

    const unsubscribe = subscribeAppTheme(() => {
      if (mounted) {
        setThemeVersion((previous) => previous + 1);
      }
    });

    void loadAppTheme().finally(() => {
      if (mounted) {
        setIsThemeReady(true);
      }
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  // let [fontsLoaded] = useFonts({
  //   "Pricedown-Bl": require("./assets/fonts/pricedown-bl.otf"),
  // });

  const onLayoutRootView = useCallback(async () => {
    if (isLoadingComplete) {
      await SplashScreen.hideAsync();
    }
  }, [isLoadingComplete]);

  if (!isLoadingComplete || !isThemeReady) {
    return null;
  } else {
    return (
      <ApolloProvider client={client}>
        <PaperProvider
          settings={{ icon: (props) => <MaterialCommunityIcons {...props} /> }}
        >
          <SafeAreaProvider onLayout={onLayoutRootView}>
              <Navigation colorScheme={colorScheme} themeVersion={themeVersion} />
            <StatusBar style={colorScheme} />
          </SafeAreaProvider>
        </PaperProvider>
      </ApolloProvider>
    );
  }
}
